import { matchers } from 'millan';
import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';

import { LanguageId } from 'src/containers/MonacoEditor/constants/languageId';

const { PN_PREFIX, PN_LOCAL_ESC, PN_LOCAL, ECHAR } = matchers;

const keywords = [
  // Cannot add '@prefix' or '@base' here because the '@' is monarch-specific.
  // Maybe there's away around that (escaping the '@' inside the string doesn't
  // do it), but, for now, it's handled in the tokenizer as a special case.
  'prefix',
  'base',
  'a',
  'true',
  'false',
  // srs keywords
  'rule',
  'if',
  'then',
];

const sharedRootTokenDefinitions: Monaco.languages.IMonarchLanguageRule[] = [
  { include: '@whitespace' },
  [/\[\s*\]/, 'identifier.iri.prefix.bnode'],
  [/[()[\]]/, '@brackets'],
  [/[;,.]/, 'delimiter'],
  [/@(prefix|base)/, 'keyword'], // special case; see keywords comment above

  // strings
  [/"/, 'string', '@string."'],
  [/'/, 'string', "@string.'"],
  [/(@)\w*/, 'identifier.langtag'], // langtag

  // catch-all case that prevents the parser from barfing at unrecognized
  // tokens (somehow `defaultToken: 'invalid'` doesn't catch this).
  [
    /[^\sa-zA-Z0-9]/,
    {
      cases: {
        '@default': 'invalid',
      },
    },
  ],

  // keywords and builtins
  [
    /[a-zA-Z]\w*/,
    {
      cases: {
        '@keywords': 'keyword',
        '@default': 'invalid',
      },
    },
  ],

  [/[0-9]*\.?[0-9]*(eE[+-]?[0-9]+)?/, 'number'],
];

// https://microsoft.github.io/monaco-editor/monarch.html
export const srsMonarchTokensProvider: Monaco.languages.IMonarchLanguage = {
  defaultToken: 'invalid',
  ignoreCase: true,
  keywords,
  brackets: [
    { open: '(', close: ')', token: 'brackets.parenthesis' },
    { open: '[', close: ']', token: 'brackets.square' },
    { open: '{', close: '}', token: 'brackets.curly' }, // for srs and trig
  ],
  PN_PREFIX,
  PN_LOCAL_ESC,
  PN_LOCAL,
  ECHAR,
  // eslint-disable-next-line no-control-regex
  valid_iriref_chars: /[^<>\\{}|^`\u0000-\u0020\s]*/,
  tokenizer: {
    root: [
      [/_:/, 'identifier.iri.prefix.bnode', '@pn_local'], // bnode
      [/\^{0,2}(@PN_PREFIX)?:/, 'identifier.iri.prefix', '@pn_local'], // prefixed IRIs
      [
        /(\^{0,2})(<)(@valid_iriref_chars)(>)/,
        [
          'identifier.iri.brackets',
          'identifier.iri.brackets',
          'identifier.iri',
          'identifier.iri.brackets',
        ],
      ],

      // srs if/then clauses
      [/if\s*{/i, '@rematch', '@ifMatcher'],
      [/then\s*{/i, '@rematch', '@thenMatcher'],

      ...sharedRootTokenDefinitions,
    ],
    ifMatcher: [
      [
        /if/i,
        { token: 'keyword', next: '@push', nextEmbedded: LanguageId.SPARQL },
      ],
      // assuming that an `if` is always followed by `then`
      [/then/i, { token: '@rematch', next: '@popall', nextEmbedded: '@pop' }],
    ],
    thenMatcher: [
      [/then/i, { token: 'keyword', next: '@push' }],
      // to get outer bracket matching, assuming no nested curly brackets
      [
        /{/,
        { token: '@brackets', next: '@push', nextEmbedded: LanguageId.SPARQL },
      ],
      [/}/, { token: '@brackets', next: '@popall', nextEmbedded: '@pop' }],
    ],
    string: [
      [/@ECHAR/, 'string.escape'], // escapes
      [/[^"']/, 'string'],
      [
        /["']/,
        {
          cases: {
            '$#==$S2': { token: 'string', next: '@pop' },
            '@default': 'string',
          },
        },
      ],
    ],
    pn_local: [
      [/@PN_LOCAL_ESC/, 'string.escape'], // escapes
      [/@PN_LOCAL/, 'identifier.iri', '@pop'],
      [/(?!@PN_LOCAL)/, 'identifier.iri', '@pop'],
    ],
    whitespace: [
      [/[\s\t\r\n]+/, 'white'],
      [/#.*$/, 'comment'],
    ],
  },
};
